import csvParse from 'csv-parse/lib/browser'
import IntlMessageFormat from 'intl-messageformat'

const cleanString = (column: string) => column.replace(/[^A-Za-z0-9]/g, '').toLowerCase()

const getColumnsIndexes = (requiredHeaderColumnNames: string[], currentCsvHeaderColumnNames: string[]) =>
  requiredHeaderColumnNames.reduce((indexes: number[], column: string) => {
    indexes.push(currentCsvHeaderColumnNames.indexOf(column))

    return indexes
  }, [])

export const validateColumns = (requiredHeaderColumnNames: string[], csvContent: string[][]) => {
  const cleanedRequiredColumnNames = requiredHeaderColumnNames.map(cleanString)
  const [csvHeaderColumnNames] = csvContent
  const cleanedCurrentColumnNames = csvHeaderColumnNames.map(cleanString)

  const missingColumns = cleanedRequiredColumnNames.reduce(
    (missed: string[], column: string) => (cleanedCurrentColumnNames.includes(column) ? missed : [...missed, column]),
    [],
  )

  const errorMessage = new IntlMessageFormat(
    'File is invalid. The following {count, plural, one {column is} other {columns are}} missing: {missingColumns}.',
  ).format({ count: missingColumns.length, missingColumns: missingColumns.join(', ') })

  return { missingColumns, errorMessage }
}

export const addCsvLineNumbers = (requiredHeaderColumnNames: string[], csvContent: string[][]) => {
  const cleanedRequiredColumnNames = requiredHeaderColumnNames.map(cleanString)
  const [csvHeaderColumnNames, ...csvRows] = csvContent
  const cleanedCurrentColumnNames = csvHeaderColumnNames.map(cleanString)

  const indexes = getColumnsIndexes(cleanedRequiredColumnNames, cleanedCurrentColumnNames)
  const resultRows = csvRows.map((column: string[], row: number) => [
    `#${row + 2}`,
    ...indexes.map((index: number) => column[index]),
  ])

  const resultColumns = ['Row', ...requiredHeaderColumnNames]

  return [resultColumns, ...resultRows]
}

export const buildCsvJsonContent = (csvContent: string[][]) => {
  const [csvHeaderNames, ...rows] = csvContent

  return rows.map((row) =>
    csvHeaderNames.reduce((result, header, index) => {
      const jsonRowContent = { ...result }
      jsonRowContent[header] = row[index]

      return jsonRowContent
    }, {}),
  )
}

export const loadCsv = ({ onError, onLoad, headers, setContentMessage, acceptedFile, reader }) =>
  csvParse(reader.result as string | Buffer, { delimiter: [',', ';'] }, (error, output) => {
    setContentMessage('')

    if (error) {
      setContentMessage('Error when trying to open the file. Check the file format and try it again.')
      onError(error)
      return
    }

    const { missingColumns, errorMessage } = validateColumns(headers, output)

    if (missingColumns.length) {
      setContentMessage(errorMessage)
      onError(errorMessage)
      return
    }

    setContentMessage(`Loaded file: ${acceptedFile.name}`)
    const arrayContent = addCsvLineNumbers(headers, output)
    const jsonContent = buildCsvJsonContent(arrayContent)
    onLoad(jsonContent)
  })
